import Service from '@ember/service';

import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { isMemberDecoratorArgs } from 'decorator-setup';

import { isFn, method } from 'rise-ui/utils/function.js';
import { queryParams } from 'rise-ui/utils/link-to.js';
import { promise, resolve } from 'rise-ui/utils/promise.js';
import { chainHash } from 'rise-ui/utils/prototype.js';

const breadcrumbNamespace = Symbol('breadcrumb');

function getBreadcrumbNamespace(target) {
  return target[breadcrumbNamespace] ??= {};
}

function getBreadcrumbProperties(instance) {
  return chainHash(instance, breadcrumbNamespace) || {};
}

/**
 * Decorate route with breadcrumbs
 *
 * Use decorator on any property/accessor/method which returns desired crumbs.
 * Use decorator once per route class.
 * @decorator
 */
export function breadcrumb(target, propertyKey, descriptor) {
  if (!isMemberDecoratorArgs(...arguments)) {
    throw new Error('@breadcrumb decorator should be applied to a property');
  }
  getBreadcrumbNamespace(target).crumbKey = propertyKey;
}

/**
 * Decorate route with breadcrumbs after some promise resolves
 *
 * Use decorator on any property/accessor/method which returns promise.
 * Method is passed model and route.
 * Use decorator once per route class.
 * @decorator
 */
export function breadcrumbResolve(target, propertyKey, descriptor) {
  if (!isMemberDecoratorArgs(...arguments)) {
    throw new Error('@breadcrumbResolve decorator should be applied to a property');
  }
  getBreadcrumbNamespace(target).resolveKey = propertyKey;
}

/**
 * Decorate route with breadcrumbs after some promise resolves
 *
 * Use decorator on any property/accessor/method which returns crumbs to use
 * after resolve.
 * Method is passed model and route.
 * Use decorator once per route class.
 * @decorator
 */
export function breadcrumbResolved(target, propertyKey, descriptor) {
  if (!isMemberDecoratorArgs(...arguments)) {
    throw new Error('@breadcrumbResolved decorator should be applied to a property');
  }
  getBreadcrumbNamespace(target).resolvedKey = propertyKey;
}

/**
 * @typedef BreadcrumbCrumble
 * @extends BreadcrumbCrumb
 * @property {[]} params Generated params for passing to linkTo helper
 */

/**
 * @typedef BreadcrumbCrumb
 * @property {string} text Display text for crumb
 * @property {string} route Route to link to
 * @property {string[]} [args] Ordered arguments for route
 * @property {{}} [query] Query-params to add
 */

/**
 * @property {BreadcrumbCrumble[]} crumbs
 */
export default class Breadcrumb extends Service {
  @service router;

  @tracked crumbs;

  constructor() {
    super(...arguments);
    this.setCrumb();

    // Remove all crumbs when route is about to change
    this.router.on('routeWillChange', () => this.clearCrumbs());

    // Set new crumbs from route path
    this.router.on('routeDidChange', transition => {
      transition.routeInfos.reduceRight((_, routeInfo) => {
        const { _route: route } = routeInfo;
        function parseCrumb(crumb) {
          if (!crumb) {
            return;
          }
          if (typeof crumb === 'string') {
            crumb = { text: crumb };
          }
          if (!crumb.route) {
            crumb.route = route.fullRouteName;
            if (!crumb.args) {
              crumb.args = routeInfo.paramNames.map(p => routeInfo.params[p]);
            }
          }
          return crumb;
        }

        const { crumbKey, resolveKey, resolvedKey } = getBreadcrumbProperties(route);
        const crumb = route[crumbKey];
        const model = route.currentModel;

        // Initial crumbs
        [].concat(method(route, crumb, crumb, model, route) || [])
          .reverse()
          .forEach(crumb => this.prependCrumb(parseCrumb(crumb)));

        // Resolved crumbs
        const resolver = route[resolveKey];
        const modelResolve = isFn(resolver) ? resolver : resolve;
        promise(modelResolve.call(route, model, route))
          .then(() => {
            if (resolvedKey in route) {
              const resolved = route[resolvedKey];
              const crumb = method(route, resolved, resolved, model, route);
              if (crumb) {
                this.replaceCrumb(...[].concat(crumb).map(parseCrumb));
              }
            }
          }).catch(e => console.error(e));

      }, null);
    });
  }

  /**
   * Store given crumb(s).
   * @param {BreadcrumbCrumb[]} crumbs Crumbs to store
   * @return {this}
   */
  setCrumb(...crumbs) {
    this.crumbs = crumbs.map(crumb => {
      if (!crumb.params) {
        crumb.params = [ crumb.route ];
        if (Array.isArray(crumb.args)) {
          crumb.params.push(...crumb.args);
        }
        if (crumb.query) {
          crumb.params.push(queryParams(crumb.query));
        }
      }
      return crumb;
    });
    return this;
  }

  /**
   * Append given crumb(s) to stored crumbs.
   * @param {BreadcrumbCrumb[]} crumbs Crumbs to append to stored crumbs
   * @return {this}
   */
  appendCrumb(...crumbs) {
    return this.setCrumb(...[ ...this.crumbs, ...crumbs ]);
  }

  /**
   * Prepend given crumb(s) to stored crumbs.
   * @param {BreadcrumbCrumb[]} crumbs Crumbs to prepend to stored crumbs
   * @return {this}
   */
  prependCrumb(...crumbs) {
    return this.setCrumb(...[ ...crumbs, ...this.crumbs ]);
  }

  /**
   * Replace given crumb(s) by route.
   * @param {BreadcrumbCrumb[]} crumbs Crumbs to store
   * @return {this}
   */
  replaceCrumb(...crumbs) {
    const byRoute = {};
    crumbs.forEach(crumb => {
      byRoute[crumb.route] = [].concat(byRoute[crumb.route] || [], crumb);
    });
    let newCrumbs = [];
    this.crumbs.forEach(c => {
      if (byRoute[c.route] === false) {
        return;
      }
      if (byRoute[c.route]) {
        newCrumbs = newCrumbs.concat(byRoute[c.route]);
        byRoute[c.route] = false;
        return;
      }
      newCrumbs.push(c);
    });
    return this.setCrumb(...newCrumbs);
  }

  /**
   * Remove given crumb(s) by route.
   * @param {BreadcrumbCrumb[]} crumbs Crumbs to remove
   * @return {this}
   */
  removeCrumb(...crumbs) {
    const byRoute = {};
    crumbs.forEach(crumb => {
      byRoute[crumb.route] = crumb;
    });
    return this.setCrumb(...this.crumbs.filter(crumb => !byRoute[crumb.route]));
  }

  clearCrumbs() {
    this.setCrumb();
  }
}
